import { Suspense } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";
import useUser from "../Util/useUser.js";
import { Switch } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";

import Protected from "./Protected.js";
import ChunkErrorBoundary from "./ChunkErrorBoundary.jsx";

import Page404 from "../Pages/NotFound/NotFound.js";
import Login from "../Pages/Login/Login.js";
import SessionExpired from "../Pages/SessionExpired/SessionExpired.js";
import ChangePassword from "../Pages/ChangePassword/ChangePassword.js";
import { finalRoutes } from "../Util/routesConfig.js";
import { componentMap } from "../Util/componentMap";

const RouteFallback = () => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
        }}
    >
        <Spinner animation="border" role="status" aria-label="Loading page">
            <span className="visually-hidden">Loading…</span>
        </Spinner>
    </div>
);

function renderRouteComponent(route, routeProps) {
    const compKey = route.component;
    if (!compKey) {
        console.error("Route has no component key:", route);
        return <div>Component not configured</div>;
    }

    const ComponentToRender = componentMap[compKey];

    if (!ComponentToRender) {
        console.error(`Component not found in componentMap: ${compKey}`, route);
        return <div>Component "{compKey}" not found</div>;
    }

    return <ComponentToRender {...routeProps} />;
}

export function createRoutesElement({ user, isLoggedIn, pathname } = {}) {
    return (
        <ChunkErrorBoundary key={pathname}>
        <Suspense fallback={<RouteFallback />}>
        <Switch>
            {finalRoutes.map((r, idx) => {
                // handle permission vs public
                if (r.permission) {
                    // permission-protected route
                    return (
                        <Route
                            key={idx}
                            path={r.path}
                            exact={r.exact !== false}
                            render={(props) => (
                                <Protected permission={r.permission} deny={r.denyPermission}>
                                    {renderRouteComponent(r, props)}
                                </Protected>
                            )}
                        />
                    );
                }

                // public route
                return (
                    <Route
                        key={idx}
                        path={r.path}
                        exact={r.exact !== false}
                        render={(props) => renderRouteComponent(r, props)}
                    />
                );
            })}

            <Route path="/session-expired" exact>
                <SessionExpired />
            </Route>
            <Route path="/login" exact>
                <Login />
            </Route>
            <Route path="/change-password" exact>
                <ChangePassword />
            </Route>
            <Route path="*">
                <Page404 />
            </Route>
        </Switch>
        </Suspense>
        </ChunkErrorBoundary>
    );
}

export const RoutesTree = () => {
    const { user, isLoggedIn } = useUser();
    const { pathname } = useLocation();
    return createRoutesElement({ user, isLoggedIn, pathname });
};

export default RoutesTree;
